import React, { FunctionComponent, useState } from 'react';
import Box from '@material-ui/core/Box';
import SignInForm from 'pages/signin/components/Form';
import { Tab, Tabs, Typography } from '@material-ui/core';
import logoMark from 'images/lns-logomark.svg';
import { storeTokens } from 'utils/auth';
import { useNavigate } from 'react-router';
import toggleNotification from 'actions/notifications';
import { useDispatch } from 'react-redux';
import {
  fetchUser,
  setCurrentSsoUser,
  setCurrentLnsUser,
  signIn
} from 'pages/signin/actions';
import camelcase from 'camelcase-keys';
import i18n from 'i18n';
import TabPanel from 'components/TabPanel';
import SignupForm from 'pages/signup/components/Form';
import { SignUpFormData } from 'pages/signup/interface';
import { signup } from 'pages/signup/actions';
import signInStyles from './styles';

const SignIn: FunctionComponent = () => {
  const classes = signInStyles();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, toggleLoading] = useState(false);
  const [currTab, setCurrTab] = useState(1);
  const [errors, setErrors] = useState({});

  const onClick = (email: string, password: string) => {
    toggleLoading(true);
    signIn(email, password)
      .then(({ token, tokenExpiresOn }) => {
        toggleLoading(false);
        storeTokens(token, tokenExpiresOn);
        fetchUser().then(fetchUserJson => {
          // Temporary until api returns camelcase json
          // Work around still necessary
          // Last check on 18Aug2021
          const currentSsoUser = camelcase(fetchUserJson);
          dispatch(
            setCurrentSsoUser(currentSsoUser as unknown as $Lns.SsoUser)
          );
          dispatch(
            setCurrentLnsUser(currentSsoUser as unknown as $Lns.SsoUser)
          );
          dispatch(
            toggleNotification(
              i18n.t('notifications.signin.success'),
              'success'
            )
          );
          navigate('/');
        });
      })
      .catch(() => {
        toggleLoading(false);
        dispatch(
          toggleNotification(i18n.t('notifications.signin.error'), 'error')
        );
      });
  };

  const onSignUp = (formData: SignUpFormData) => {
    toggleLoading(true);
    signup(formData)
      .then(({ errors: jsonErrors }) => {
        toggleLoading(false);
        if (jsonErrors) {
          const newErrors = jsonErrors.reduce(
            (accu: Record<string, unknown>, b: $Lns.ObjectKey) => {
              accu[b.source] = b.message;
              return accu;
            },
            {}
          );

          setErrors(newErrors);
          dispatch(
            toggleNotification(i18n.t('notifications.signup.error'), 'error')
          );
        } else {
          dispatch(
            toggleNotification(
              i18n.t('notifications.signup.success'),
              'success'
            )
          );
          setCurrTab(1);
        }
      })
      .catch(() => {
        toggleLoading(false);
        dispatch(
          toggleNotification(
            i18n.t('notifications.signup.unavailable'),
            'error'
          )
        );
      });
  };

  const handleChange = (
    event: React.ChangeEvent<Record<string, unknown>>,
    newValue: number
  ) => {
    setCurrTab(newValue);
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Box display="flex" justifyContent="center" marginBottom="12px">
          <img src={logoMark} width="50px" alt="Lns Logo" />
        </Box>
        <Typography className={classes.title}>
          Log in to your account
        </Typography>
        <Typography className={classes.subtitle}>
          Welcome back! Please enter your details.
        </Typography>
      </Box>
      <Box className={classes.body}>
        <Tabs value={currTab} onChange={handleChange}>
          <Tab className={classes.tab} label={i18n.t('button.signup')} />
          <Tab className={classes.tab} label={i18n.t('button.signin')} />
        </Tabs>
        <TabPanel value={currTab} index={0}>
          <SignupForm onSave={onSignUp} isLoading={isLoading} errors={errors} />
        </TabPanel>
        <TabPanel value={currTab} index={1}>
          <SignInForm onClick={onClick} isLoading={isLoading} />
        </TabPanel>
      </Box>
    </Box>
  );
};

export default SignIn;
